/**
 * One provider row in Connected Accounts: the provider mark, its name, whether
 * it is connected, and at most one action (Connect / Disconnect).
 *
 * Purely presentational -- which action a provider gets, and whether it is
 * allowed, is decided by the Sign-in & Security screen.
 */

import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { SOCIAL_PROVIDER_META } from '@/features/auth/providers';
import type { SocialProvider } from '@/features/auth/sign-in-social.types';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { View } from 'react-native';

export interface ConnectedAccountAction {
  /** Button copy. */
  label: string;
  accessibilityLabel: string;
  variant: 'outline' | 'destructive';
  /** Always explicit so the disabled state is never ambiguous. */
  disabled: boolean;
  loading: boolean;
  onPress: () => void;
}

interface ConnectedAccountRowProps {
  provider: SocialProvider;
  connected: boolean;
  /** `null` when no action is offered (e.g. an unlinked, disabled provider). */
  action: ConnectedAccountAction | null;
  /** Short explanation shown under the row, e.g. why Disconnect is disabled. */
  note?: string;
  isLast?: boolean;
}

const STATUS_CONNECTED = 'Connected';
const STATUS_NOT_CONNECTED = 'Not connected';

export function ConnectedAccountRow({
  provider,
  connected,
  action,
  note,
  isLast = false,
}: ConnectedAccountRowProps) {
  const meta = SOCIAL_PROVIDER_META[provider];

  return (
    <View
      testID={`connected-account-${provider}`}
      className={`py-4 ${!isLast ? 'border-b border-border' : ''}`}
    >
      <View className="flex-row items-center">
        <View className="w-8 items-center mr-3">
          <Ionicons name={meta.icon} size={22} className="text-foreground" />
        </View>

        <View className="flex-1 mr-3">
          <Text className="text-base text-foreground">{meta.name}</Text>
          <Text className="text-sm text-muted-foreground">
            {connected ? STATUS_CONNECTED : STATUS_NOT_CONNECTED}
          </Text>
        </View>

        {action ? (
          <Button
            size="sm"
            variant={action.variant}
            disabled={action.disabled}
            loading={action.loading}
            onPress={action.onPress}
            accessibilityLabel={action.accessibilityLabel}
            testID={`connected-account-${provider}-action`}
          >
            {action.label}
          </Button>
        ) : null}
      </View>

      {note ? (
        <Text className="text-xs mt-2 text-muted-foreground">{note}</Text>
      ) : null}
    </View>
  );
}
